/*
  Variations, wave 3 (screen 11) — pure helpers.
  - variationTrail(vo)          the approval trail, oldest first (#6); a VO saved before trails existed gets one
                                "Proposed" step from when it was created (same rule as backend/variation_trail.py)
  - variationFollowUp(vo, today) { days, since, party } when the last step is "Sent to consultant" more than
                                FOLLOW_UP_DAYS ago and the VO is still Proposed (#6 reminder)
  - variationInvoicing(vo, invoices) what the Invoicing column shows (#7): received / submitted / scheduled /
                                not invoiced, with the invoiced share of the VO value
  - attachmentUrl(projectId, file) the download link of a trail paper or a site photo
*/
import { formatNumber } from '@/lib/format';

export const FOLLOW_UP_DAYS = 14;

export const TRAIL_STEPS = {
  proposed: { label: 'Proposed', tone: 'gray' },
  sent: { label: 'Sent to consultant', tone: 'blue' },
  revise: { label: 'Revise & resubmit', tone: 'amber' },
  approved: { label: 'Approved', tone: 'navy' },
  rejected: { label: 'Rejected', tone: 'red' },
};

/** The steps a person can record (Proposed comes from creating the VO). */
export const RECORDABLE_STEPS = ['sent', 'approved', 'revise', 'rejected'];

const DAY_MS = 86400000;
const text = (value) => String(value ?? '').trim();
const isoDay = (value) => {
  const match = text(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : '';
};
const dayNumber = (iso) => {
  const day = isoDay(iso);
  if (!day) return null;
  const [y, m, d] = day.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};

/** Today in GST as YYYY-MM-DD (the app's calendar). */
export function todayGst(now = new Date()) {
  return new Date(now.getTime() + 4 * 3600000).toISOString().slice(0, 10);
}

export function variationTrail(variation = {}) {
  const stored = Array.isArray(variation.trail) ? variation.trail.filter((entry) => entry && typeof entry === 'object') : [];
  let trail = stored;
  if (!trail.length) {
    const created = isoDay(variation.created_at);
    trail = created ? [{
      id: `proposed-${variation.id || ''}`,
      step: 'proposed',
      label: TRAIL_STEPS.proposed.label,
      date: created,
      by: text(variation.created_by),
      party: '',
      reference: '',
      note: '',
      attachment: null,
      recorded_at: text(variation.created_at),
      derived: true,
    }] : [];
  }
  return [...trail]
    .map((entry) => ({ ...entry, label: entry.label || TRAIL_STEPS[entry.step]?.label || entry.step }))
    .sort((a, b) => `${isoDay(a.date)}|${a.recorded_at || ''}`.localeCompare(`${isoDay(b.date)}|${b.recorded_at || ''}`));
}

export function variationFollowUp(variation = {}, today = todayGst()) {
  const status = text(variation.status) || 'Proposed';
  if (status !== 'Proposed') return null;
  // a Proposed step never ends the wait (a VO entered today but sent weeks ago has its Proposed step today):
  // it waits from the last Sent until a Revise / Approved / Rejected answer
  const trail = variationTrail(variation).filter((entry) => entry.step !== 'proposed');
  const last = trail[trail.length - 1];
  if (!last || last.step !== 'sent') return null;
  const since = dayNumber(last.date);
  const now = dayNumber(today);
  if (since === null || now === null) return null;
  const days = now - since;
  return days > FOLLOW_UP_DAYS ? { days, since: isoDay(last.date), party: text(last.party) } : null;
}

/** The trail step that decided the VO (approved / rejected), if one was recorded. */
export function decisionStep(variation = {}) {
  const trail = variationTrail(variation);
  return [...trail].reverse().find((entry) => entry.step === 'approved' || entry.step === 'rejected') || null;
}

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * The Invoicing column of one VO (#7): `kind` received | submitted | scheduled | none, the amounts, the invoiced
 * share (0–100) of the VO value and a short label ("39,375 received", "6,510 submitted", "Not invoiced").
 */
export function variationInvoicing(variation = {}, invoices = []) {
  const value = Math.abs(num(variation.total_value ?? variation.financial_impact));
  const own = (invoices || []).filter((invoice) => invoice.variation_id === variation.id);
  const issued = own.filter((invoice) => (text(invoice.status) || 'Not Started') !== 'Not Started');
  const received = issued.filter((invoice) => invoice.status === 'Payment Received').reduce((sum, invoice) => sum + Math.abs(num(invoice.amount)), 0);
  const invoiced = issued.reduce((sum, invoice) => sum + Math.abs(num(invoice.amount)), 0);
  const submitted = invoiced - received;
  const scheduled = own.filter((invoice) => (text(invoice.status) || 'Not Started') === 'Not Started').reduce((sum, invoice) => sum + Math.abs(num(invoice.amount)), 0);
  const percent = value > 0 ? Math.max(0, Math.min(100, Math.round((invoiced / value) * 100))) : (invoiced > 0 ? 100 : 0);
  const amount = (n) => formatNumber(n, { maxDecimals: 2 });
  let kind = 'none';
  let label = 'Not invoiced';
  let tone = 'gray';
  if (invoiced > 0 && received >= invoiced) {
    kind = 'received';
    label = `${amount(received)} received`;
    tone = 'green';
  } else if (invoiced > 0) {
    kind = 'submitted';
    label = received > 0 ? `${amount(received)} received · ${amount(submitted)} submitted` : `${amount(invoiced)} submitted`;
    tone = 'amber';
  } else if (scheduled > 0) {
    kind = 'scheduled';
    label = `${amount(scheduled)} scheduled`;
    tone = 'gray';
  }
  return {
    kind, label, tone, value, invoiced, received, submitted, scheduled, percent,
    count: own.length,
    remaining: Math.max(0, value - invoiced),
    // only an approved VO is invoiced: a Proposed / Rejected one shows "Not invoiced" without Raise
    canRaise: text(variation.status) === 'Approved' && value > 0 && invoiced + scheduled < value - 0.005,
  };
}

export const attachmentUrl = (projectId, file) => (file ? `/api/projects/${projectId}/variations/attachments/${file}` : '');

/** "Raised on site · Villa 4 (F) · Majlis" — the source line of a VO raised from site (#8). */
export function siteSourceText(variation = {}) {
  const source = variation.source;
  if (!source || source.kind !== 'site') return '';
  return ['Raised on site', text(source.location), text(source.by)].filter(Boolean).join(' · ');
}
